<template>
    <div class="space-y-6">
        <!-- Single unified search field for users or teams -->
        <div>
            <label for="search-contacts" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                {{ $t('schedule.eventForm.searchContacts') }}
            </label>
            <div class="relative">
                <div class="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                    <svg class="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                        <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 19l-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"/>
                    </svg>
                </div>
                <input 
                    type="search" 
                    id="search-contacts" 
                    class="block w-full p-4 ps-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-indigo-500 dark:focus:border-indigo-500" 
                    :placeholder="$t('schedule.eventForm.placeholders.searchContacts')" 
                    v-model="searchText"
                />
            </div>

            <div class="mt-3">
                <div v-if="contactLoading">
                    <Loading class="w-full"/>
                </div>
                <div v-else>
                    <div class="block mb-2 text-sm font-medium text-red-700 dark:text-white text-center" v-if="hasContactError">
                        {{ $t('schedule.eventForm.errors.noContacts') }}
                    </div>

                    <div v-for="contact in filteredContactResults" :key="contactKey(contact)" class="flex items-center justify-between p-3 mb-2 bg-gray-50 rounded-lg dark:bg-gray-700">
                        <div class="flex items-center">
                            <div class="flex-shrink-0">
                                <svg v-if="isTeam(contact)" class="w-6 h-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                                </svg>
                                <svg v-else class="w-6 h-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                </svg>
                            </div>
                            <div class="ml-3">
                                <p class="text-sm font-medium text-gray-900 dark:text-white">{{ contactLabel(contact) }}</p>
                                <p v-if="isTeam(contact)" class="text-xs text-gray-500 dark:text-gray-400">{{ contact.memberCount }} {{ $t('schedule.eventForm.members') }}</p>
                            </div>
                        </div>
                        <button @click="handleAddContact(contact)" class="inline-flex items-center px-3 py-2 text-sm font-medium text-center text-white bg-indigo-700 rounded-lg hover:bg-indigo-800 focus:ring-4 focus:outline-none focus:ring-indigo-300 dark:bg-indigo-600 dark:hover:bg-indigo-700 dark:focus:ring-indigo-800">
                            {{ $t('schedule.eventForm.add') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Selected Participants Summary -->
        <div v-if="selectedUsers.length > 0 || selectedTeams.length > 0" class="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 class="text-sm font-medium text-gray-900 dark:text-white mb-3">{{ $t('schedule.eventForm.selectedParticipants') }}</h4>
            
            <!-- Selected Users -->
            <div v-if="selectedUsers.length > 0" class="mb-4">
                <h5 class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{{ $t('schedule.eventForm.users') }}</h5>
                <div class="space-y-2">
                    <div v-for="user in selectedUsers" 
                        :key="'selected-user-' + user.id"
                        class="flex items-center justify-between p-2 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg">
                        <div class="flex items-center">
                            <svg class="w-4 h-4 text-indigo-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                            </svg>
                            <span class="text-sm text-gray-900 dark:text-white">{{ showUserNameEmail(user) }}</span>
                        </div>
                        <button @click="removeUser(user)" 
                            class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Selected Teams -->
            <div v-if="selectedTeams.length > 0">
                <h5 class="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{{ $t('schedule.eventForm.teams') }}</h5>
                <div class="space-y-2">
                    <div v-for="team in selectedTeams" 
                        :key="'selected-team-' + team._id"
                        class="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div class="flex items-center">
                            <svg class="w-4 h-4 text-green-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z" />
                            </svg>
                            <span class="text-sm text-gray-900 dark:text-white">{{ team.name }} ({{ team.memberCount }} {{ $t('schedule.eventForm.members') }})</span>
                        </div>
                        <button @click="removeTeam(team)" 
                            class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300">
                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits, watch, defineExpose } from 'vue';
import Loading from '@/components/TriadLoad.vue';
import toastStore from "@/store/toast.store";

const props = defineProps({
    excludeUsers: {
        type: Array,
        default: () => []
    },
    excludeTeams: {
        type: Array,
        default: () => []
    },
    initialUsers: {
        type: Array,
        default: () => []
    },
    initialTeams: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['participants-updated', 'add-participant', 'remove-participant']);

// State
const searchText = ref('');
const contactLoading = ref(false);
const contactResults = ref([]);
const selectedUsers = ref([]);
const selectedTeams = ref([]);
let debounceTimer = null;

// Helpers to identify contact type
const isTeam = (c) => {
    if (!c) return false;
    if (c.isTeam && c.isTeam === true) return true;
    return false;
};

const contactKey = (c) => c.id || c._id || c.contactId || JSON.stringify(c);
const contactLabel = (c) => {
    if (!c) return '';
    if (isTeam(c)) return c.name || c.teamName || c._id;
    return c.name ? `${c.name} (${c.email || ''})` : (c.email || c.id || 'Unknown');
};

// Filter results against excludes and already selected
const filteredContactResults = computed(() => {
    return contactResults.value.filter(contact => {
        if (!contact) return false;
        if (isTeam(contact)) {
            const id = contact.id || contact._id || contact.teamId || '';
            if (!id) return false;
            if (props.excludeTeams.includes(id)) return false;
            if (selectedTeams.value.some(t => (t.id || t._id || t.teamId) === id)) return false;
            return true;
        } else {
            const id = contact.id || contact._id || contact.email || '';
            if (!id) return false;
            if (props.excludeUsers.includes(id)) return false;
            if (selectedUsers.value.some(u => (u.id || u._id || u.email) === id)) return false;
            return true;
        }
    });
});

const hasContactError = computed(() => {
    if (filteredContactResults.value.length === 0 && searchText.value && !contactLoading.value && searchText.value.length >= 2) {
        return true;
    }
    return false;
});

// Fetch contacts from unified endpoint
const fetchContacts = async (term) => {
    contactLoading.value = true;
    try {
        const encoded = encodeURIComponent(term);
        const resp = await fetch(`/api/members/contacts?term=${encoded}`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' }
        });
        if (resp.ok) {
            const data = await resp.json();
            // Expect data to be an array of mixed contacts
            if (Array.isArray(data)) {
                contactResults.value = data;
            } else {
                contactResults.value = [];
            }
        } else {
            console.error('Unexpected response searching contacts', resp.status);
            contactResults.value = [];
            toastStore.dispatch('addMessage', { message: 'Failed to search contacts', icon: 'error', ttl: 5000 });
        }
    } catch (err) {
        console.error('Error fetching contacts', err);
        contactResults.value = [];
        toastStore.dispatch('addMessage', { message: 'Failed to search contacts', icon: 'error', ttl: 5000 });
    } finally {
        contactLoading.value = false;
    }
};

// Watcher with debounce to trigger search when user types at least 2 chars
watch(searchText, (val) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    if (!val || val.length < 2) {
        contactResults.value = [];
        contactLoading.value = false;
        return;
    }
    debounceTimer = setTimeout(() => {
        fetchContacts(val.trim());
    }, 300);
});

const handleAddContact = (contact) => {
    if (isTeam(contact)) {
        const id = contact._id || contact.teamId;
        if (!selectedTeams.value.some(t => (t._id || t.teamId) === id)) {
            selectedTeams.value.push(contact);
            emit('add-participant', { id: id, type: 'team', data: contact });
            emitParticipantsUpdate();
        }
    } else {
        const id = contact.id || contact._id || contact.email;
        if (!selectedUsers.value.some(u => (u.id || u._id || u.email) === id)) {
            selectedUsers.value.push(contact);
            emit('add-participant', { id: id, type: 'user', data: contact });
            emitParticipantsUpdate();
        }
    }
    // Clear search after adding
    searchText.value = '';
    contactResults.value = [];
};

const removeUser = (user) => {
    const id = user.id || user._id || user.email;
    selectedUsers.value = selectedUsers.value.filter(u => (u.id || u._id || u.email) !== id);
    emit('remove-participant', { id: id, type: 'user' });
    emitParticipantsUpdate();
};

const removeTeam = (team) => {
    const id = team._id || team.teamId;
    selectedTeams.value = selectedTeams.value.filter(t => (t._id || t.teamId) !== id);
    emit('remove-participant', { id: id, type: 'team' });
    emitParticipantsUpdate();
};

const emitParticipantsUpdate = () => {
    emit('participants-updated', {
        users: selectedUsers.value,
        teams: selectedTeams.value
    });
};

// Initialize with initial values
watch(() => props.initialUsers, (users) => {
    if (users) {
        selectedUsers.value = [...users];
    }
}, { immediate: true });

watch(() => props.initialTeams, (teams) => {
    if (teams) {
        selectedTeams.value = [...teams];
    }
}, { immediate: true });

const showUserNameEmail = (user) => {
    return user.name ? `${user.name} (${user.email})` : user.email;
};

defineExpose({
    reset: () => {
        console.log('Resetting event participant search');
        searchText.value = '';
        contactResults.value = [];
        selectedUsers.value = [];
        selectedTeams.value = [];
    }
});
</script>
